import { MIN_EXPLOSION_DMG } from '../constants.js';
import { carveCircle } from '../terrain/mask.js';
import type { GameState, WeaponId } from '../types.js';
import { knockback } from './character.js';
import { emit } from './events.js';

export interface ExplosionOpts {
  /** Terrain carve radius multiplier (watermelon digs deeper). */
  carveScale?: number;
  /** Knockback strength multiplier. */
  knockScale?: number;
  /** Team credited for damage/kills. */
  sourceTeamId: number;
  /** Weapon flavor for the explosion event (client VFX/audio scale). */
  weaponId?: WeaponId;
}

/**
 * The full detonation package: carve terrain, shake, damage entities,
 * fling everyone, wreck props, mark deaths.
 */
export function detonate(
  state: GameState,
  x: number,
  y: number,
  radius: number,
  maxDmg: number,
  opts: ExplosionOpts,
): { damaged: { entityId: number; dmg: number }[] } {
  const carveScale = opts.carveScale ?? 1;
  const knockScale = opts.knockScale ?? 1;

  // Terrain first — it's part of the event stream clients apply immediately.
  // Rim jitter is seeded from the circle itself (identity carve), so every
  // machine rasterizes this exact crater to the same pixels — no rng draw,
  // no stream drift, masks identical across the lockstep fleet.
  const carveR = radius * carveScale * 0.9;
  carveCircle(state.terrain, x, y, carveR);
  const circle = { x, y, r: carveR };
  state.carveLog.push(circle);
  emit(state, { type: 'terrain_carved', circles: [circle] });
  emit(state, { type: 'explosion', x, y, r: radius, weaponId: opts.weaponId ?? 'coconut' });
  emit(state, { type: 'shake', trauma: Math.min(1, radius / 130) });

  const srcTeam = state.teams.find((t) => t.id === opts.sourceTeamId);
  const damaged: { entityId: number; dmg: number }[] = [];

  // Characters.
  for (const e of state.entities) {
    const d = Math.hypot(e.x - x, e.y - 16 - y);
    if (d >= radius) continue;

    if (!e.alive) {
      // Corpses obey the shockwave too: no damage, but the blast shoves them
      // — gravity (stepCorpse, every tick) takes it from there.
      const shove = (1 - d / radius) * 430 * knockScale;
      if (shove > 0) knockback(e, x, y, shove);
      continue;
    }

    let dmg = maxDmg * (1 - d / radius);
    if (dmg < MIN_EXPLOSION_DMG) continue;
    dmg = Math.max(0, Math.round(dmg - e.armor));

    e.hp = Math.max(0, e.hp - dmg);
    e.pose = 'hurt';
    e.poseTimer = 0.45;
    const strength = (1 - d / radius) * 430 * knockScale;
    // knockScale 0 (lemon) means the victim does not shift an inch — skip the
    // call entirely so even the onGround reset never happens.
    if (strength > 0) knockback(e, x, y, strength);

    if (srcTeam) srcTeam.score.damageDealt += dmg;
    e.lastHitTeamId = opts.sourceTeamId;
    damaged.push({ entityId: e.id, dmg });
    emit(state, { type: 'damage', entityId: e.id, amount: dmg, hpAfter: e.hp, x: e.x, y: e.y - 34 });

    if (e.hp <= 0) killEntity(state, e.id, 'explosion', opts.sourceTeamId);
  }

  // Props (destructible scenery).
  for (const p of state.props) {
    if (!p.alive) continue;
    const cx = p.x;
    const cy = p.y - p.h / 2;
    const d = Math.hypot(cx - x, cy - y);
    if (d >= radius + Math.max(p.w, p.h) * 0.4) continue;
    p.hp -= Math.round(maxDmg * (1 - Math.max(0, d) / (radius + 20)));
    if (p.hp <= 0) {
      p.alive = false;
      emit(state, { type: 'prop_destroyed', propId: p.id, kind: p.kind, x: p.x, y: cy });
    }
  }

  return { damaged };
}

/**
 * The single place raw damage is applied to a character: HP, pose, score,
 * last-hit credit, the `damage` event, and death.
 *
 * The engine's explosion/hazard/fall paths have their own knockback and
 * bookkeeping around it, but bee stings and anything else that just "hits"
 * route through here so a sting can kill and still fire a proper death event.
 */
export function stingEntity(
  state: GameState,
  e: GameState['entities'][number],
  amount: number,
  sourceTeamId: number | null,
  x: number,
  y: number,
): number {
  if (!e.alive) return 0;
  const dmg = Math.max(0, Math.round(amount));
  if (dmg <= 0) return 0;
  e.hp = Math.max(0, e.hp - dmg);
  e.pose = 'hurt';
  e.poseTimer = 0.4;
  if (sourceTeamId != null) {
    e.lastHitTeamId = sourceTeamId;
    const src = state.teams.find((t) => t.id === sourceTeamId);
    if (src) src.score.damageDealt += dmg;
  }
  emit(state, { type: 'damage', entityId: e.id, amount: dmg, hpAfter: e.hp, x, y });
  if (e.hp <= 0) killEntity(state, e.id, 'hazard', sourceTeamId);
  return dmg;
}

/** Mark an entity dead and check team eliminations. */
export function killEntity(
  state: GameState,
  entityId: number,
  cause: 'explosion' | 'fall' | 'drown' | 'oob' | 'hazard',
  killerTeamId: number | null,
): void {
  const e = state.entities.find((en) => en.id === entityId);
  if (!e || !e.alive) return;
  e.alive = false;
  e.hp = 0;
  e.pose = 'dead';
  e.poseTimer = 1.5;
  e.vx = e.vx || 0;
  e.onGround = false;
  // A corpse does not keep flying its jump arc — gravity takes over now.
  e.script = null;

  if (killerTeamId !== null && killerTeamId !== e.teamId) {
    const killer = state.teams.find((t) => t.id === killerTeamId);
    if (killer) killer.score.kills += 1;
  }

  emit(state, { type: 'death', entityId: e.id, cause, x: e.x, y: e.y });
  emit(state, { type: 'voice', entityId: e.id, kind: 'die' });

  // Team elimination?
  const team = state.teams.find((t) => t.id === e.teamId);
  if (team) {
    const anyAlive = state.entities.some((en) => en.teamId === team.id && en.alive);
    if (!anyAlive) team.alive = false;
  }
}
